import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Spin,
  Table,
  Typography,
} from "antd";
import {
  PlusOutlined,
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../helpers/axiosConfig";
import { playlist, collections } from "../../static/api";
import {
  getPlaylists,
  getPlaylist,
  addPlaylistToList,
  removePlaylistFromList,
  clearCurrentPlaylist,
} from "../../store/slices/playlistSlice";

import "./style.css";

const { Title, Text } = Typography;

export const PlaylistsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { playlists, currentPlaylist, loading, detailLoading } = useSelector(
    (state) => state.playlist
  );

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [userCollections, setUserCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);

  useEffect(() => {
    dispatch(getPlaylists());
  }, [dispatch]);

  // ─── Create playlist ───
  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axiosInstance.post(playlist, { name: newName.trim() });
      dispatch(addPlaylistToList(res.data));
      setCreateModalOpen(false);
      setNewName("");
      notification.success({ message: "Playlist created" });
    } catch (e) {
      notification.error({
        message: "Failed to create playlist",
        description: e.response?.data?.message || e.message,
      });
    }
  };

  // ─── Delete playlist ───
  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this playlist?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await axiosInstance.delete(`${playlist}/${id}`);
          dispatch(removePlaylistFromList(id));
          if (currentPlaylist?.id === id) dispatch(clearCurrentPlaylist());
          notification.success({ message: "Playlist deleted" });
        } catch (e) {
          notification.error({
            message: "Failed to delete",
            description: e.response?.data?.message || e.message,
          });
        }
      },
    });
  };

  // ─── Select playlist for editing ───
  const handleSelect = (id) => {
    dispatch(getPlaylist(id));
  };

  // ─── Back to list ───
  const handleBack = () => {
    dispatch(clearCurrentPlaylist());
  };

  // ─── Play playlist ───
  const handlePlay = (id) => {
    navigate(`/playlist-player/${id}`);
  };

  // ─── Add item modal: fetch user's collections ───
  const openAddItemModal = async () => {
    setAddItemModalOpen(true);
    setCollectionsLoading(true);
    try {
      const res = await axiosInstance.get(collections);
      // Filter out collections already in the playlist
      const existingIds = (currentPlaylist?.items || []).map(
        (item) => item.collectionId
      );
      setUserCollections(
        res.data.filter((c) => !existingIds.includes(c.id))
      );
    } catch (e) {
      notification.error({ message: "Failed to load compilations" });
    } finally {
      setCollectionsLoading(false);
    }
  };

  // ─── Add collection to playlist ───
  const handleAddItem = async (collectionId) => {
    try {
      await axiosInstance.post(`${playlist}/${currentPlaylist.id}/items`, {
        collectionId,
        playDuration: 30,
      });
      dispatch(getPlaylist(currentPlaylist.id));
      setUserCollections((prev) =>
        prev.filter((c) => c.id !== collectionId)
      );
      notification.success({ message: "Compilation added" });
    } catch (e) {
      notification.error({
        message: "Failed to add",
        description: e.response?.data?.message || e.message,
      });
    }
  };

  // ─── Remove item ───
  const handleRemoveItem = async (itemId) => {
    try {
      await axiosInstance.delete(
        `${playlist}/${currentPlaylist.id}/items/${itemId}`
      );
      dispatch(getPlaylist(currentPlaylist.id));
    } catch (e) {
      notification.error({ message: "Failed to remove item" });
    }
  };

  // ─── Update play duration ───
  const handleUpdateDuration = async (itemId, playDuration) => {
    try {
      await axiosInstance.patch(
        `${playlist}/${currentPlaylist.id}/items/${itemId}`,
        { playDuration }
      );
    } catch (e) {
      notification.error({ message: "Failed to update duration" });
    }
  };

  // ─── Rename playlist ───
  const handleRename = async (name) => {
    try {
      await axiosInstance.patch(`${playlist}/${currentPlaylist.id}`, { name });
      dispatch(getPlaylist(currentPlaylist.id));
      dispatch(getPlaylists());
    } catch (e) {
      notification.error({ message: "Failed to rename" });
    }
  };

  // ─── DETAIL VIEW ───
  if (currentPlaylist) {
    const items = currentPlaylist.items || [];

    const columns = [
      {
        title: "",
        dataIndex: "sort",
        width: 40,
        render: () => <MenuOutlined style={{ cursor: "grab", color: "#999" }} />,
      },
      {
        title: "Compilation",
        dataIndex: "collectionName",
        key: "collectionName",
      },
      {
        title: "Play Duration (sec)",
        dataIndex: "playDuration",
        key: "playDuration",
        width: 160,
        render: (val, record) => (
          <InputNumber
            min={5}
            max={3600}
            defaultValue={val}
            onBlur={(e) =>
              handleUpdateDuration(record.id, parseInt(e.target.value) || 30)
            }
          />
        ),
      },
      {
        title: "",
        key: "actions",
        width: 60,
        render: (_, record) => (
          <DeleteOutlined
            style={{ color: "#ff4d4f", cursor: "pointer" }}
            onClick={() => handleRemoveItem(record.id)}
          />
        ),
      },
    ];

    return (
      <div className="playlists-page">
        <div className="playlists-detail-header">
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Back
          </Button>
          <Title
            level={4}
            editable={{ onChange: handleRename }}
            style={{ margin: 0, flex: 1, marginLeft: 16 }}
          >
            {currentPlaylist.name}
          </Title>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => handlePlay(currentPlaylist.id)}
            disabled={items.length === 0}
          >
            Play
          </Button>
        </div>

        {detailLoading ? (
          <div className="playlists-loading">
            <Spin />
          </div>
        ) : (
          <>
            <Table
              dataSource={items.map((item) => ({ ...item, key: item.id }))}
              columns={columns}
              pagination={false}
              locale={{ emptyText: "No compilations added yet" }}
              className="playlists-table"
            />
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={openAddItemModal}
              style={{ marginTop: 16 }}
              block
            >
              Add Compilation
            </Button>
          </>
        )}

        {/* Add compilation modal */}
        <Modal
          title="Add Compilation"
          open={addItemModalOpen}
          onCancel={() => setAddItemModalOpen(false)}
          footer={null}
        >
          {collectionsLoading ? (
            <div className="playlists-loading">
              <Spin />
            </div>
          ) : userCollections.length === 0 ? (
            <Empty description="No compilations available" />
          ) : (
            <div className="playlists-collection-list">
              {userCollections.map((c) => (
                <div key={c.id} className="playlists-collection-item">
                  <Text>{c.name}</Text>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleAddItem(c.id)}
                  >
                    Add
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      </div>
    );
  }

  // ─── LIST VIEW ───
  return (
    <div className="playlists-page">
      <div className="playlists-header">
        <Title level={3} style={{ margin: 0 }}>
          Playlists
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create Playlist
        </Button>
      </div>

      {loading ? (
        <div className="playlists-loading">
          <Spin size="large" />
        </div>
      ) : playlists.length === 0 ? (
        <Empty
          description="No playlists yet"
          style={{ marginTop: 80 }}
        >
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            Create your first playlist
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {playlists.map((pl) => (
            <Col key={pl.id} xs={24} sm={12} md={8} lg={8}>
              <Card
                className="playlists-card"
                actions={[
                  <PlayCircleOutlined
                    key="play"
                    onClick={() => handlePlay(pl.id)}
                  />,
                  <EditOutlined
                    key="edit"
                    onClick={() => handleSelect(pl.id)}
                  />,
                  <DeleteOutlined
                    key="delete"
                    onClick={() => handleDelete(pl.id)}
                  />,
                ]}
              >
                <Card.Meta
                  title={pl.name}
                  description={`${pl.itemCount || 0} compilation${
                    pl.itemCount !== 1 ? "s" : ""
                  }`}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Create playlist modal */}
      <Modal
        title="Create Playlist"
        open={createModalOpen}
        onOk={handleCreate}
        onCancel={() => {
          setCreateModalOpen(false);
          setNewName("");
        }}
        okText="Create"
        okButtonProps={{ disabled: !newName.trim() }}
      >
        <Input
          placeholder="Playlist name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleCreate}
          autoFocus
        />
      </Modal>
    </div>
  );
};
